/**
 * ExtranetResetPassword — Fase 4 Extranet KB
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-04 v1.2.0 - Validação de senha forte no cliente (zod)
 *            com checklist em tempo real abaixo do campo, alinhada às regras
 *            do backend; traduz mensagens de erro cruas do servidor para PT-BR.
 *   RELEASE: 2026-07-09 v1.1.0 - Corrigido "Auth session missing!":
 *            materializa sessão a partir dos tokens do hash (recovery/invite)
 *            via supabase.auth.setSession() antes de chamar updateUser.
 *            Limpa o hash da URL e faz signOut após atualizar a senha para
 *            não contaminar a sessão do painel principal.
 *   RELEASE: 2026-06-xx v1.0.0 - Versão inicial.
 *
 * Fluxo:
 *   1. Supabase redireciona o link do email para esta rota com tokens no hash
 *      (#access_token=...&refresh_token=...&type=recovery|invite).
 *   2. Como o cliente global usa detectSessionInUrl:false (intencional), fazemos
 *      o parse manual e chamamos setSession() para poder chamar updateUser.
 *   3. Após atualizar senha, deslogamos e mandamos para o login da extranet.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { resolveAuthError, type AuthErrorInfo } from '@/lib/extranet/authErrors';
import { ExtranetAuthAlert } from '@/components/kb/extranet/ExtranetAuthAlert';
// Regras de senha forte centralizadas + checklist em tempo real
import { strongPasswordSchema, translatePasswordError } from '@/lib/passwordRules';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';

export default function ExtranetResetPassword() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [alertInfo, setAlertInfo] = useState<AuthErrorInfo | null>(null);


  // Materializa a sessão a partir do hash da URL (tokens de recovery/invite)
  useEffect(() => {
    const initSession = async () => {
      try {
        const hash = window.location.hash?.startsWith('#')
          ? window.location.hash.slice(1)
          : window.location.hash;
        const params = new URLSearchParams(hash || '');

        const errorDescription = params.get('error_description') || params.get('error');
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (errorDescription) {
          logger.warn('[ExtranetResetPassword] Erro no link:', errorDescription);
          setLinkError(decodeURIComponent(errorDescription));
          setInitializing(false);
          return;
        }

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            logger.error('[ExtranetResetPassword] setSession falhou:', error.message);
            setLinkError('Link inválido ou expirado. Solicite um novo.');
            setInitializing(false);
            return;
          }
          // Remove tokens da URL por segurança
          window.history.replaceState(null, '', window.location.pathname);
          setSessionReady(true);
          setInitializing(false);
          return;
        }

        // Sem tokens no hash: verifica se já existe sessão ativa (edge case)
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          setSessionReady(true);
        } else {
          setLinkError('Link inválido ou expirado. Solicite um novo.');
        }
      } catch (err) {
        logger.error('[ExtranetResetPassword] Erro ao inicializar sessão:', err);
        setLinkError('Não foi possível validar o link.');
      } finally {
        setInitializing(false);
      }
    };
    initSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionReady) return;
    setAlertInfo(null);
    // Validação client-side com as MESMAS regras do backend (evita erro cru)
    const passwordResult = strongPasswordSchema.safeParse(password);
    if (!passwordResult.success) {
      // Reaproveita o alerta padrão de senha fraca, com a descrição detalhada do zod
      const baseInfo = resolveAuthError({ code: 'weak_password' });
      setAlertInfo({
        ...baseInfo,
        description: passwordResult.error.errors[0].message,
      });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setSubmitting(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyErr = error as any;
      setAlertInfo(
        resolveAuthError({
          code: anyErr?.code,
          status: anyErr?.status,
          // Rede de segurança: traduz mensagens cruas do backend para PT-BR
          message: translatePasswordError(error.message),
        }),
      );
      return;
    }
    // Desloga para não contaminar a aba do painel do admin
    await supabase.auth.signOut();
    setSubmitting(false);
    toast.success('Senha definida! Faça login.');
    navigate(`/extranet/${tenantSlug}/login`);
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Helmet>
        <title>Redefinir senha</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Card className="w-full max-w-md p-6 space-y-5">
        <h1 className="text-xl font-semibold text-center">Nova senha</h1>

        {initializing && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Validando link...
          </div>
        )}

        {!initializing && linkError && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
              <p className="text-destructive">{linkError}</p>
            </div>
            <Link
              to={`/extranet/${tenantSlug}/esqueci-senha`}
              className="block text-center text-sm text-primary hover:underline"
            >
              Solicitar novo link
            </Link>
          </div>
        )}

        {!initializing && !linkError && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <ExtranetAuthAlert info={alertInfo} hrefPrefix={`/extranet/${tenantSlug}`} />
            <div className="space-y-1.5">
              <Label htmlFor="password">Nova senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                maxLength={200}
                autoComplete="new-password"
                disabled={!sessionReady || submitting}
              />
              {/* Checklist de requisitos em tempo real (evita erro do backend) */}
              {password.length > 0 && <PasswordRequirements password={password} />}
            </div>
            <Button type="submit" className="w-full" disabled={!sessionReady || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Atualizar senha
            </Button>
            <div className="text-center text-sm">
              <Link
                to={`/extranet/${tenantSlug}/login`}
                className="text-muted-foreground hover:text-foreground"
              >
                Voltar ao login
              </Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
